/**
 * usePlan() — single source of truth for current tenant's plan + limits + capabilities,
 * reading from @my360/shared. Wraps the existing /api/billing endpoint so any component
 * can ask "does this user have analytics?" without prop-drilling.
 *
 * Usage:
 *   const { plan, limits, can, storage, isLoading } = usePlan();
 *   if (!can.whiteLabel) showLockBadge();
 */
import { useMemo } from 'react';
import { useBillingInfo } from '@/api/queries';
import {
  getPlan,
  getPlanLimits,
  formatStorage,
  type PlanDefinition,
  type PlanLimits,
} from '@my360/shared';

export interface PlanCapabilities {
  whiteLabel: boolean;
  vr: boolean;
  analytics: boolean;
  customDomain: boolean;
  passwordProtect: boolean;
  leadCapture: boolean;
}

export interface PlanStorageInfo {
  /** Bytes currently used */
  used: number;
  /** Bytes allowed (-1 = unlimited) */
  limit: number;
  /** Pretty-printed used (e.g. "1.4 GB") */
  usedLabel: string;
  /** Pretty-printed limit (e.g. "25 GB") or "Ilimitado" */
  limitLabel: string;
  /** 0..100 (rounded). 0 when unlimited. */
  percent: number;
  /** True when over 80% — UI should warn */
  warning: boolean;
  /** True when over 95% — UI should error */
  critical: boolean;
  /** True when limit is -1 */
  unlimited: boolean;
}

export interface UsePlanResult {
  /** Full plan definition (slug, name, tagline, price, etc). Defaults to Free while loading. */
  plan: PlanDefinition;
  /** Limits enforced server-side. */
  limits: PlanLimits;
  /** Boolean capability flags — easy "if (can.whiteLabel)" guards. */
  can: PlanCapabilities;
  /** Storage usage info for UI bars/warnings. */
  storage: PlanStorageInfo;
  /** True while billing info is loading. UI may render skeleton. */
  isLoading: boolean;
  /** Raw current tenant plan slug from DB ('beta', 'pro', etc) */
  slug: string;
}

const ZERO_STORAGE: PlanStorageInfo = {
  used: 0,
  limit: 0,
  usedLabel: '0 MB',
  limitLabel: formatStorage(0),
  percent: 0,
  warning: false,
  critical: false,
  unlimited: false,
};

export function usePlan(): UsePlanResult {
  const { data, isLoading } = useBillingInfo();
  const tenant = data?.data?.tenant;
  const usage = data?.data?.usage;

  return useMemo(() => {
    const slug = tenant?.plan || 'free';
    const plan = getPlan(slug);
    const limits = getPlanLimits(slug);

    const can: PlanCapabilities = {
      whiteLabel: limits.whiteLabel,
      vr: limits.vr,
      analytics: limits.analytics,
      customDomain: limits.customDomain,
      passwordProtect: limits.passwordProtect,
      leadCapture: limits.leadCapture,
    };

    let storage: PlanStorageInfo;
    if (limits.maxStorage === -1) {
      storage = {
        used: usage?.storageUsed ?? 0,
        limit: -1,
        usedLabel: formatStorage(usage?.storageUsed ?? 0),
        limitLabel: 'Ilimitado',
        percent: 0,
        warning: false,
        critical: false,
        unlimited: true,
      };
    } else {
      const used = usage?.storageUsed ?? 0;
      const percent = Math.min(100, Math.round((used / limits.maxStorage) * 100));
      storage = {
        used,
        limit: limits.maxStorage,
        usedLabel: formatStorage(used),
        limitLabel: formatStorage(limits.maxStorage),
        percent,
        warning: percent >= 80 && percent < 95,
        critical: percent >= 95,
        unlimited: false,
      };
    }

    return { plan, limits, can, storage, isLoading, slug };
  }, [tenant?.plan, usage?.storageUsed, isLoading]);
}

// Re-export the placeholder so callers can use it for skeleton states
export { ZERO_STORAGE };
